import {Inject, Injectable, InjectionToken, OnDestroy} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {
  ApiMode,
  CallbackPayload,
  ConfigService,
  ConnectMode,
  ConnectPayload,
  EventTypes,
  MessageBusEventPayload,
  MessageBusService,
  Organization,
} from '@shared-library';
import {Subscription} from 'rxjs';

// Organization context
const ORG_FEATURE_FLAGS_TAG = 'org.feature_flags';
const ORG_ID_TAG = 'org.id';
const ORG_NAME_TAG = 'org.name';
const ORG_PLAN_TAG = 'org.plan';
const PUBLIC_ID_TAG = 'org.public_id';

// Connection context
const CONNECTION_ID_TAG = 'org_connection.id';

// External context
const EXTERNAL_ID_TAG = 'external.id';
const EXTERNAL_STATE_TAG = 'external.state';

// Runtime configuration
const API_MODE_TAG = 'api_mode';
const CONNECT_MODE_TAG = 'connect_mode';
const SDK_MODE_TAG = 'sdk_mode';

// UI configuration
const SEARCH_ONLY_TAG = 'ui.search_only';
const SHOW_SPLASH_TAG = 'ui.show_splash';
const STATIC_BACKDROP_TAG = 'ui.static_backdrop';
const TEFCA_MODE_TAG = 'ui.tefca_mode';

const METRIC_NAMES = {
  widgetLifecycle: 'embed.widget.lifecycle',
  widgetInitializationDuration: 'embed.widget.initialization_duration',
  cookieProbeResult: 'embed.cookie_probe.result',
  storageAccessResult: 'embed.storage_access.result',
  authCookieResult: 'embed.auth_cookie.result',
  popupOpen: 'embed.popup.open',
  callbackResult: 'embed.callback.result',
  callbackDuration: 'embed.callback.duration',
} as const;

// Sentry v10 merges isolation-scope attributes into every metric. Explicitly
// omit redundant, per-request, and customer-provided values while retaining
// org.id as the one stable tenant dimension for operational breakdowns.
const METRIC_CONTEXT_EXCLUDED_ATTRIBUTES = [
  ORG_FEATURE_FLAGS_TAG,
  ORG_NAME_TAG,
  PUBLIC_ID_TAG,
  CONNECTION_ID_TAG,
  EXTERNAL_ID_TAG,
  EXTERNAL_STATE_TAG,
] as const;

type MetricAttributes = Record<string, string | number | boolean>;

export interface SentryMetricSink {
  count: typeof Sentry.metrics.count;
  distribution: typeof Sentry.metrics.distribution;
}

export const SENTRY_METRIC_SINK = new InjectionToken<SentryMetricSink>('Sentry metric sink', {
  providedIn: 'root',
  factory: () => Sentry.metrics,
});

export const SENTRY_METRIC_CLOCK = new InjectionToken<() => number>('Sentry metric monotonic clock', {
  providedIn: 'root',
  factory: () => () => performance.now(),
});

export type WidgetExperience = 'search_only' | 'tefca' | 'direct';
export type WidgetInitializationPath = 'cookie_not_required' | 'cookies_supported' | 'storage_access_fallback';
export type WidgetConfigErrorReason =
  | 'invalid_public_id'
  | 'org_config_failed'
  | 'tefca_not_enabled'
  | 'cookie_probe_failed'
  | 'unknown';
export type WidgetBlockedReason = 'cookies_blocked';
export type CookieProbeScopeMetric = 'all' | 'regular';
export type CookieProbeOutcome = 'supported' | 'blocked' | 'error';
export type StorageAccessOutcome =
  | 'granted'
  | 'denied'
  | 'unavailable'
  | 'granted_but_cookies_blocked'
  | 'verification_error';
export type AuthCookieOutcome = 'present' | 'missing';
export type AuthCookieCheck = 'immediate' | 'after_retry';
export type PopupFlow = 'connection' | 'identity_verification' | 'storage_access';
export type CallbackFlow = 'connection' | 'identity_verification';
export type CallbackTransport = ConnectMode.Popup | ConnectMode.Websocket;
export type CallbackOutcome = 'received' | 'timeout' | 'transport_error' | 'invalid_payload';

interface CallbackStart {
  attempt: CallbackAttempt;
  startedAt: number;
  transport: CallbackTransport;
}

export type CallbackAttempt = number;

@Injectable({
  providedIn: 'root',
})
export class SentryContextService implements OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly widgetStages = new Set<'started' | 'initialized' | 'complete'>();
  private readonly callbackStarts = new Map<CallbackFlow, CallbackStart>();
  private nextCallbackAttempt: CallbackAttempt = 0;
  private widgetExperience: WidgetExperience | 'unknown' = 'unknown';
  private widgetStartedAt?: number;
  private widgetFailureRecorded = false;
  private configContextInitialized = false;

  constructor(
    private readonly configService: ConfigService,
    private readonly messageBusService: MessageBusService,
    @Inject(SENTRY_METRIC_SINK) private readonly metrics: SentryMetricSink,
    @Inject(SENTRY_METRIC_CLOCK) private readonly now: () => number,
  ) {
    // Capture the public ID before AppComponent populates the system config so
    // errors during early bootstrap can still be attributed to an organization.
    this.setIdentifier(
      PUBLIC_ID_TAG,
      new URLSearchParams(window.location.search).get('public-id') || undefined,
    );

    this.subscriptions.add(
      this.configService.systemConfigSubject.subscribe((config) => {
        // ConfigService starts with placeholder test-mode values. Wait until the
        // embed publishes a real configuration so early telemetry is not
        // attributed to those defaults and the URL public ID is not cleared.
        const isPlaceholderConfig = !config.publicId
          && config.searchOnly === undefined
          && config.tefcaMode === undefined
          && config.connectMode === undefined;
        if (!this.configContextInitialized && isPlaceholderConfig) {
          return;
        }
        this.configContextInitialized = true;

        this.setConfigTag(PUBLIC_ID_TAG, config.publicId);
        this.setConfigTag(EXTERNAL_ID_TAG, config.externalId);
        this.setConfigTag(EXTERNAL_STATE_TAG, config.externalState);
        this.setConfigTag(CONNECTION_ID_TAG, config.reconnectOrgConnectionId);
        this.setConfigTag(API_MODE_TAG, config.apiMode);
        this.setConfigTag(SEARCH_ONLY_TAG, config.searchOnly);
        this.setConfigTag(TEFCA_MODE_TAG, config.tefcaMode);
        this.setConfigTag(STATIC_BACKDROP_TAG, config.staticBackdrop);
        this.setConfigTag(SHOW_SPLASH_TAG, config.showSplash);
        this.setConfigTag(SDK_MODE_TAG, config.sdkMode);
        this.setConfigTag(CONNECT_MODE_TAG, config.connectMode);
        this.updateOrganizationContext(config.org);
      }),
    );

    this.subscriptions.add(
      this.messageBusService.messageBusSubject.subscribe((event) => this.updateEventContext(event)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setConnectionContext(connectionId?: string): void {
    this.setIdentifier(CONNECTION_ID_TAG, connectionId);
  }

  recordWidgetStarted(experience: WidgetExperience, apiMode: ApiMode): void {
    if (this.widgetStages.has('started')) {
      return;
    }

    this.widgetExperience = experience;
    this.widgetStartedAt = this.now();
    this.widgetStages.add('started');
    this.count(METRIC_NAMES.widgetLifecycle, {
      stage: 'started',
      experience,
      api_mode: apiMode,
    });
  }

  recordWidgetInitialized(initializationPath: WidgetInitializationPath): void {
    if (this.widgetFailureRecorded || this.widgetStages.has('initialized')) {
      return;
    }

    this.widgetStages.add('initialized');
    const attributes = {
      stage: 'initialized',
      experience: this.widgetExperience,
      initialization_path: initializationPath,
    };
    this.count(METRIC_NAMES.widgetLifecycle, attributes);
    this.distributionSince(METRIC_NAMES.widgetInitializationDuration, this.widgetStartedAt, attributes);
  }

  recordWidgetConfigError(reason: WidgetConfigErrorReason): void {
    this.recordWidgetFailure('config_error', reason);
  }

  recordWidgetBlocked(reason: WidgetBlockedReason): void {
    this.recordWidgetFailure('blocked', reason);
  }

  recordCookieProbeResult(
    scope: CookieProbeScopeMetric,
    outcome: CookieProbeOutcome,
    fallbackAvailable: boolean,
  ): void {
    this.count(METRIC_NAMES.cookieProbeResult, {
      scope,
      outcome,
      fallback_available: fallbackAvailable,
    });
  }

  recordStorageAccessResult(outcome: StorageAccessOutcome): void {
    this.count(METRIC_NAMES.storageAccessResult, {outcome});
  }

  recordAuthCookieResult(outcome: AuthCookieOutcome, check: AuthCookieCheck): void {
    this.count(METRIC_NAMES.authCookieResult, {outcome, check});
  }

  recordPopupOpen(flow: PopupFlow, transport: CallbackTransport, opened: boolean): void {
    this.count(METRIC_NAMES.popupOpen, {
      flow,
      transport,
      outcome: opened ? 'opened' : 'blocked',
    });
  }

  recordCallbackStarted(flow: CallbackFlow, transport: CallbackTransport): CallbackAttempt {
    const attempt = ++this.nextCallbackAttempt;
    this.callbackStarts.set(flow, {attempt, startedAt: this.now(), transport});
    return attempt;
  }

  recordCallbackReceived(flow: CallbackFlow, attempt: CallbackAttempt): void {
    this.finishCallback(flow, attempt, 'received');
  }

  recordCallbackError(flow: CallbackFlow, attempt: CallbackAttempt, error: unknown): void {
    this.finishCallback(flow, attempt, this.classifyCallbackError(error));
  }

  private updateOrganizationContext(org?: Organization): void {
    this.setIdentifier(ORG_ID_TAG, org?.id);
    this.setIdentifier(ORG_NAME_TAG, org?.name);
    this.setIdentifier(ORG_PLAN_TAG, org?.plan);
    this.setIdentifier(
      ORG_FEATURE_FLAGS_TAG,
      org ? JSON.stringify(org.feature_flags ?? []) : undefined,
    );
  }

  private updateEventContext(event: MessageBusEventPayload | null): void {
    if (!event) {
      return;
    }

    if (event.event_type === EventTypes.EventTypeWidgetConfigError) {
      this.recordWidgetConfigError('unknown');
      return;
    }

    if (event.event_type === EventTypes.EventTypeWidgetComplete) {
      this.recordWidgetComplete();
      return;
    }

    if (Array.isArray(event.data)) {
      return;
    }

    if (event.event_type === EventTypes.EventTypeConnectionPending) {
      const connectData = event.data as ConnectPayload;
      this.setConnectionContext(connectData.org_connection_id);
      return;
    }

    if (
      event.event_type === EventTypes.EventTypeConnectionSuccess ||
      event.event_type === EventTypes.EventTypeConnectionFailed
    ) {
      const callbackData = event.data as CallbackPayload;
      this.setConnectionContext(callbackData.org_connection_id);
    }
  }

  private recordWidgetFailure(stage: 'config_error' | 'blocked', reason: WidgetConfigErrorReason | WidgetBlockedReason): void {
    if (this.widgetFailureRecorded || this.widgetStages.has('initialized')) {
      return;
    }

    this.widgetFailureRecorded = true;
    this.count(METRIC_NAMES.widgetLifecycle, {
      stage,
      experience: this.widgetExperience,
      reason,
    });
  }

  private recordWidgetComplete(): void {
    if (this.widgetStages.has('complete')) {
      return;
    }

    this.widgetStages.add('complete');
    this.count(METRIC_NAMES.widgetLifecycle, {
      stage: 'complete',
      experience: this.widgetExperience,
    });
  }

  private finishCallback(flow: CallbackFlow, attempt: CallbackAttempt, outcome: CallbackOutcome): void {
    const callbackStart = this.callbackStarts.get(flow);
    if (!callbackStart || callbackStart.attempt !== attempt) {
      return;
    }

    this.callbackStarts.delete(flow);
    const attributes = {
      flow,
      transport: callbackStart.transport,
      outcome,
    };
    this.count(METRIC_NAMES.callbackResult, attributes);
    this.distributionSince(METRIC_NAMES.callbackDuration, callbackStart.startedAt, attributes);
  }

  private classifyCallbackError(error: unknown): CallbackOutcome {
    let payload: unknown = error;
    if (error instanceof Error) {
      payload = error.message;
    }

    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return 'invalid_payload';
      }
    }

    if (!payload || typeof payload !== 'object') {
      return 'invalid_payload';
    }

    const errorCode = (payload as {error?: unknown}).error;
    if (typeof errorCode !== 'string' || errorCode.length === 0) {
      return 'invalid_payload';
    }
    if (errorCode === 'timeout') {
      return 'timeout';
    }
    if (errorCode === 'websocket_connection') {
      return 'transport_error';
    }

    // Any other structured error was successfully delivered by the backend.
    // Its business outcome is already covered by authoritative backend metrics.
    return 'received';
  }

  private count(name: string, attributes: MetricAttributes): void {
    this.captureMetric(() => this.metrics.count(name, 1, {attributes}));
  }

  private distributionSince(name: string, startedAt: number | undefined, attributes: MetricAttributes): void {
    if (startedAt === undefined) {
      return;
    }

    const duration = Math.max(0, this.now() - startedAt);
    if (!Number.isFinite(duration)) {
      return;
    }

    this.captureMetric(() => this.metrics.distribution(name, duration, {
      unit: 'millisecond',
      attributes,
    }));
  }

  private captureMetric(capture: () => void): void {
    Sentry.withScope((scope) => {
      // An undefined current-scope attribute overrides the inherited isolation
      // value, and the metric serializer then omits the key. The outer scope is
      // unchanged, so logs and traces keep their useful correlation context.
      for (const attribute of METRIC_CONTEXT_EXCLUDED_ATTRIBUTES) {
        scope.setAttribute(attribute, undefined);
      }
      capture();
    });
  }

  private setConfigTag(tag: string, value: string | boolean | undefined | null): void {
    const normalizedValue = value === undefined || value === null || value === ''
      ? undefined
      : String(value);
    this.setIdentifier(tag, normalizedValue);
  }

  private setIdentifier(tag: string, value?: string): void {
    const normalizedValue = value === '' ? undefined : value;
    const isolationScope = Sentry.getIsolationScope();
    const activeSpan = Sentry.getActiveSpan();

    // Tags make the identifiers searchable on events. Scope attributes also
    // propagate them to Sentry logs and spans.
    Sentry.setTag(tag, normalizedValue);
    if (normalizedValue === undefined) {
      isolationScope.removeAttribute(tag);
      activeSpan?.setAttribute(tag, undefined);
      return;
    }

    isolationScope.setAttribute(tag, normalizedValue);
    activeSpan?.setAttribute(tag, normalizedValue);
  }
}
